import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ShaktiBoothWiseSlipSend = () => {
    const [booths, setBooths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const { appId, language, boothNo } = useAppId();
    const navigate = useNavigate();

    // Get booth number and append comma at the end if not already present
    let localBoothNos = boothNo || localStorage.getItem('boothNo') || localStorage.getItem('saktiKendraBooths') || "";
    
    // Append comma at the end if not already present
    if (localBoothNos && !localBoothNos.endsWith(',')) {
        localBoothNos = localBoothNos + ',';
    }
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Booth Wise Slip Send",
            loadingData: "Loading Booth Data...",
            searchPlaceholder: "Search by Booth Number...",
            boothNo: "Booth No:",
            total: "Total",
            send: "Send",
            noResults: "No booths found matching",
            noData: "No booth data available."
        },
        hi: {
            title: "बूथ वार स्लिप भेजें",
            loadingData: "बूथ डेटा लोड हो रहा है...",
            searchPlaceholder: "बूथ नंबर द्वारा खोजें...",
            boothNo: "बूथ नं:",
            total: "कुल",
            send: "भेजा गया",
            noResults: "इससे मेल खाने वाला कोई बूथ नहीं मिला",
            noData: "कोई बूथ डेटा उपलब्ध नहीं है।"
        },
        gu: {
            title: "બૂથ વાઇઝ સ્લિપ મોકલો",
            loadingData: "બૂથ ડેટા લોડ થઈ રહ્યો છે...",
            searchPlaceholder: "બૂથ નંબર દ્વારા શોધો...",
            boothNo: "બૂથ નં:",
            total: "કુલ",
            send: "મોકલેલ",
            noResults: "કોઈ બૂથ મળ્યું નથી",
            noData: "કોઈ બૂથ ડેટા ઉપલબ્ધ નથી."
        }
    };

    const txt = t[lang] || t.en;

    useEffect(() => {
        if (appId) {
            fetchBoothData();
        } else {
            navigate("/");
        }
    }, [appId, navigate]);

    const fetchBoothData = async () => {
        setLoading(true);
        try {
            const payload = {
                app_id: String(appId),
                booth_no: String(localBoothNos)
            };

            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_slip_send_dash_for_saktikendra`, payload);

            if (response.data.success && response.data.data && response.data.data.result) {
                setBooths(response.data.data.result);
            } else {
                setBooths([]);
            }
        } catch (err) {
            console.error("API Error:", err);
            setBooths([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredBooths = booths.filter(booth => {
        if (!searchTerm) return true;
        return booth.booth_no?.toString().includes(searchTerm);
    });

    // Helper component for the Circular Progress exactly like the image
    const CircularProgress = ({ total, send }) => {
        const safeTotal = Number(total) || 0;
        const safeSend = Number(send) || 0;
        const percentage = safeTotal > 0 ? ((safeSend / safeTotal) * 100).toFixed(1) : 0;
        
        const radius = 38;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (percentage / 100) * circumference;

        return (
            <div className="progress-container">
                <svg className="progress-svg" width="100" height="100" viewBox="0 0 100 100">
                    {/* Background Track */}
                    <circle 
                        cx="50" cy="50" r={radius} 
                        fill="none" 
                        stroke="#f1f5f9" 
                        strokeWidth="8" 
                    />
                    {/* Foreground Progress */}
                    <circle 
                        cx="50" cy="50" r={radius} 
                        fill="none" 
                        stroke="#8b5cf6" 
                        strokeWidth="8" 
                        strokeDasharray={circumference} 
                        strokeDashoffset={strokeDashoffset} 
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                    />
                </svg>
                <div className="progress-text">{percentage}%</div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="app-container p-4">
                <Header title={txt.title} />
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #f8fafc; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #8b5cf6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
            </div>
        );
    }

    return (
        <div className="app-container p-4">
            <Header title={txt.title} />

            {/* Professional Search Bar */}
            <div className="filter-container mt-4 mb-4">
                <div className="search-wrapper">
                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Premium Booth Grid Matching the Provided Image */}
            <div className="booth-grid">
                {filteredBooths.length > 0 ? (
                    filteredBooths.map((booth, index) => (
                        <div 
                            key={index} 
                            className="booth-card"
                            onClick={() => navigate('/booth_wise_slip_sending', { state: { booth_no: booth.booth_no } })}
                        >
                            
                            {/* Header */}
                            <h3 className="card-header-title">
                                {txt.boothNo} {booth.booth_no}
                            </h3>
                            
                            {/* Circular Percentage Indicator */}
                            <CircularProgress total={booth.total_voter} send={booth.send} />

                            {/* Bottom Stats Row */}
                            <div className="stats-row">
                                <div className="stat-item text-left">
                                    <span className="stat-label">{txt.total}</span>
                                    <span className="stat-value">{booth.total_voter || 0}</span>
                                </div>
                                <div className="stat-item text-right">
                                    <span className="stat-label">{txt.send}</span>
                                    <span className="stat-value">{booth.send || 0}</span>
                                </div>
                            </div>

                        </div>
                    ))
                ) : (
                    <div className="no-data-message">
                        <div className="no-data-icon">📊</div>
                        <div className="no-data-text">
                            {searchTerm ? `${txt.noResults} "${searchTerm}"` : txt.noData}
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- SEARCH BAR STYLES --- */
                .filter-container {
                    display: flex;
                    justify-content: center;
                    width: 100%;
                }

                .search-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 500px;
                }

                .search-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 20px;
                    height: 20px;
                }

                .search-input {
                    width: 100%;
                    padding: 14px 44px 14px 48px;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 15px;
                    color: #1e293b;
                    background-color: #ffffff;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }

                .search-input:focus {
                    border-color: #8b5cf6;
                    box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
                }

                .clear-search-btn {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #f1f5f9;
                    border: none;
                    color: #64748b;
                    padding: 6px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .clear-search-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }

                /* --- BOOTH GRID & CARD STYLES MATCHING IMAGE --- */
                .booth-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 24px;
                }

                .booth-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 24px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.02);
                    border: 1px solid #f8fafc;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }

                .booth-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
                }

                .card-header-title {
                    font-size: 18px;
                    font-weight: 800;
                    color: #1e293b;
                    margin: 0 0 24px 0;
                    letter-spacing: 0.2px;
                }

                /* Circular Progress Styles */
                .progress-container {
                    position: relative;
                    width: 100px;
                    height: 100px;
                    margin-bottom: 28px;
                }

                .progress-svg {
                    transform: rotate(-90deg);
                }

                .progress-text {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 18px;
                    font-weight: 800;
                    color: #8b5cf6;
                }

                /* Bottom Row Styles */
                .stats-row {
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    padding: 0 8px;
                }

                .stat-item {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .text-left {
                    align-items: flex-start;
                }

                .text-right {
                    align-items: flex-end;
                }

                .stat-label {
                    font-size: 14px;
                    font-weight: 700;
                    color: #94a3b8;
                }

                .stat-value {
                    font-size: 18px;
                    font-weight: 900;
                    color: #1e293b;
                }

                /* --- EMPTY STATE --- */
                .no-data-message {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border: 1px dashed #cbd5e1;
                }

                .no-data-icon {
                    font-size: 54px;
                    margin-bottom: 16px;
                }

                .no-data-text {
                    font-size: 16px;
                    font-weight: 600;
                    color: #64748b;
                }

                /* --- MOBILE RESPONSIVENESS --- */
                @media (max-width: 640px) {
                    .booth-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .booth-card {
                        padding: 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default ShaktiBoothWiseSlipSend;